import { useTranslation } from '@gimlite/common';
import {
  Blurb,
  BlurbType,
  Icon,
  Row,
  Space,
  Write,
  Zone,
} from '@gimlite/ui-v1';
import { useMemo } from 'react';
import { iconGlobalEntity } from '../../common/mapper/icon.mapper';
import {
  ContractBadgesType,
  useContractBadges,
} from '../../common/utils/contract-badges.hook';
import { CredentialMiniGroupType } from '../../components/credential/credential.component';

export const statusHelper = ({
  stopDate,
  startDate,
  isEnabled,
}: {
  stopDate: Date;
  startDate: Date;
  isEnabled: boolean;
}): ContractMotoristLineType.Data.Status => {
  if (isEnabled && stopDate >= new Date() && startDate <= new Date()) {
    return 'ACTIVE';
  }

  if (!isEnabled && stopDate >= new Date() && startDate <= new Date()) {
    return 'SUSPENDED';
  }

  if (stopDate < new Date() && startDate < new Date()) {
    return 'EXPIRED';
  }

  return 'UPCOMING';
};

export declare namespace ContractMotoristLineType {
  type Props = {
    data: {
      id?: string;
      isStopped?: boolean;
      parkingId?: string;
      firstName: string | null;
      lastName: string | null;
      status: ContractMotoristLineType.Data.Status;
      contractId: string | null;
      carCounter?: [number, number];
      motoristId?: string | null;
      isInside?: boolean;
      isUpcoming?: boolean;
      credentialsList?: React.ReactElement<CredentialMiniGroupType.Props>;
      apbNext?: boolean;
      classification?: string | null;
      category?: string;
    };

    config?: {
      action?: boolean;
      context?: 'tree' | 'flat';
      icon?: 'solo' | 'pool';
    };
  };

  namespace Data {
    type Status = 'ACTIVE' | 'SUSPENDED' | 'EXPIRED' | 'UPCOMING';
  }
}

export const ContractMotoristLine = ({
  data: {
    id,
    firstName,
    lastName,
    status,
    contractId,
    carCounter,
    isStopped,
    parkingId,
    motoristId,
    isInside,
    credentialsList,
    isUpcoming,
    apbNext,
    category,
    classification,
  },
  config: { action = true, context = 'flat', icon = 'pool' } = {},
}: ContractMotoristLineType.Props) => {
  const { t, lang } = useTranslation();

  let identityOptions: Pick<BlurbType.Props['data'], 'subcontain'> = {};
  if (credentialsList) {
    identityOptions = { ...identityOptions, subcontain: credentialsList };
  }

  const states: [ContractBadgesType.Data.State] = useMemo(() => {
    const all: [ContractBadgesType.Data.State] = [status];
    if (isUpcoming) all.push('UPCOMING');
    if (isStopped) all.push('EXPIRED');
    if (isInside) all.push('PRESENT');
    if (isStopped || isUpcoming) all.shift();
    return all;
  }, [isInside, isUpcoming, isStopped]);

  const badges = useContractBadges({ states });

  const counterOver = useMemo(() => {
    if (!carCounter) return false;
    return carCounter[0] > carCounter[1];
  }, [carCounter]);

  return (
    <Zone
      config={{
        gap: {
          x: 1,
          y: 0,
        },
        zones: [['icon', 'identity', 'car', 'empty']],
        rows: ['1fr'],
        columns: ['min-content', '1fr', '100px', '65px', 'min-content'],
        vertical: 'center',
        horizontal: 'center',
      }}
    >
      <Zone.Area config={{ area: 'icon' }}>
        <Icon
          config={{
            size: 'list',
            type:
              icon === 'pool'
                ? iconGlobalEntity.motoristContractPool
                : iconGlobalEntity.motoristContractSolo,
          }}
        ></Icon>
      </Zone.Area>

      <Zone.Area config={{ area: 'identity' }}>
        <Blurb
          data={{
            ...identityOptions,
            title:
              `${firstName || ''} ${lastName || ''}`.trim() || t('unknown'),
            description: contractId
              ? `${contractId} ${classification ? ` • ${classification}` : ''}`
              : '',
            badge: [
              ...badges,
              motoristId
                ? {
                    text: t('verified').toUpperCase(),
                    color: 'success',
                  }
                : null,
            ].filter(Boolean) as BlurbType.Props['data']['badge'],
          }}
        />
      </Zone.Area>

      <Zone.Area config={{ area: 'car' }}>
        <Row config={{ horizontal: 'end', vertical: 'center' }}>
          {carCounter ? (
            <>
              <Write
                config={{
                  wrap: false,
                  mode: 'key-small-bold',
                  color: counterOver ? 'error' : 'text',
                }}
                data={{ item: `${carCounter[0]} / ${carCounter[1]}` }}
              />

              <Space
                config={{
                  count: 0.5,
                  way: 'horizontal',
                }}
              />
              <Icon
                config={{
                  type: 'faCarSolid',
                  size: 'xxsmall',
                  color: counterOver ? 'error' : 'text',
                }}
              />
            </>
          ) : (
            <></>
          )}
        </Row>
      </Zone.Area>

      <Zone.Area config={{ area: 'empty' }}>
        <></>
      </Zone.Area>
    </Zone>
  );
};
